import React, { createContext, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { useReducerAsync } from 'hooks/useReducerAsync'
import googleAuthorizationApi, { googleAssetsApi } from 'api/connect/google'

const GOOGLE_ADS_BILLING_URL = 'https://ads.google.com/aw/billing/summary'

const GOOGLE_SESSIONS_KEY = 'google_sessions'

const IDENTITY_VERIFICATION_NEEDS_ATTENTION = ['PENDING_REVIEW', 'PENDING_USER_ACTION', 'UNSPECIFIED']

export function isIdentityVerificationOk(identityVerificationStatus) {
  return !IDENTITY_VERIFICATION_NEEDS_ATTENTION.includes(identityVerificationStatus)
}

export function get_google_sessions_key() {
  const clientId = localStorage.getItem('clientId')
  return `${GOOGLE_SESSIONS_KEY}_${clientId}`
}

export const GoogleConnectContext = createContext(null)
export const GoogleConnectDispatchContext = createContext(null)

export const googleSteps = {
  IDLE: 'idle',
  ACCOUNT_SELECTION: 'account_selection',
  INVITATION_PENDING: 'invitation_pending',
  NO_ACCOUNTS: 'no_accounts',
}

export const googleActions = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_MODAL_ERROR: 'SET_MODAL_ERROR',
  CLEAR_MODAL_ERROR: 'CLEAR_MODAL_ERROR',
  SET_STATUS: 'SET_STATUS',
  SET_ACCOUNTS: 'SET_ACCOUNTS',
  SET_STEP: 'SET_STEP',
  SET_SELECTED_ACCOUNT: 'SET_SELECTED_ACCOUNT',
  SET_INVITATION: 'SET_INVITATION',
  SET_INVITATION_TIMED_OUT: 'SET_INVITATION_TIMED_OUT',
  SET_ASSETS: 'SET_ASSETS',
  SET_JUST_SETTLED: 'SET_JUST_SETTLED',
  SET_ANONYMOUS: 'SET_ANONYMOUS',
  SET_GOOGLE_SESSION: 'SET_GOOGLE_SESSION',
  RESET_TO_IDLE: 'RESET_TO_IDLE',

  GET_STATUS: 'GET_STATUS',
  GET_ASSETS: 'GET_ASSETS',
  AUTHORIZE: 'AUTHORIZE',
  SELECT_ACCOUNT: 'SELECT_ACCOUNT',
  POLL_INVITATION: 'POLL_INVITATION',
  DISCONNECT: 'DISCONNECT',
  TOGGLE_ASSET: 'TOGGLE_ASSET',
  CREATE_ACCOUNT: 'CREATE_ACCOUNT',
  REFRESH_BILLING: 'REFRESH_BILLING',
  SET_CONFIRM_CONNECTION_IN_FLIGHT: 'SET_CONFIRM_CONNECTION_IN_FLIGHT',
}

export const initialState = {
  loading: false,
  error: null,
  modalError: null,
  step: googleSteps.IDLE,
  partnerConnectionType: null, // null | 'default' | 'disconnected'
  connections: [],
  accounts: [],
  selectedCustomerId: null,
  selectedAccountName: null,
  invitationStatus: null, // PENDING | ACTIVE | REFUSED
  invitationTimedOut: false,
  billingValid: null,
  accountStatus: null, // enabled | not_enabled | unknown
  identityVerificationStatus: null,
  identityVerificationActionUrl: null,
  // true while a confirmConnection request (poll tick or manual refresh) is in flight,
  // so the two call sites never overlap
  confirmConnectionInFlight: false,
  assets: [],
  justSettled: false, // true when connection fully settles (triggers test campaign auto-prompt)
  isAnonymous: false,
  googleSession: {
    platformUserId: null,
    loggedIn: false,
  },
}

export const reducer = (state, action) => {
  switch (action.type) {
    case googleActions.SET_LOADING:
      return { ...state, loading: true, error: null }
    case googleActions.SET_ERROR:
      return { ...state, loading: false, error: action.payload }
    case googleActions.SET_MODAL_ERROR:
      return { ...state, loading: false, modalError: action.payload }
    case googleActions.CLEAR_MODAL_ERROR:
      return { ...state, modalError: null }
    case googleActions.SET_STATUS: {
      const { data, error } = action.payload
      return {
        ...state,
        loading: false,
        partnerConnectionType: data?.partner_connection_type ?? null,
        connections: data?.connections ?? initialState.connections,
        error: error ?? state.error,
      }
    }
    case googleActions.SET_ACCOUNTS:
      return { ...state, loading: false, accounts: action.payload, error: null }
    case googleActions.SET_STEP:
      return { ...state, step: action.payload }
    case googleActions.SET_SELECTED_ACCOUNT: {
      const p = action.payload
      if (typeof p === 'string') return { ...state, selectedCustomerId: p }
      return {
        ...state,
        selectedCustomerId: p.customerId ?? state.selectedCustomerId,
        selectedAccountName: p.accountName ?? state.selectedAccountName,
      }
    }
    case googleActions.SET_INVITATION:
      return {
        ...state,
        loading: false,
        invitationStatus: action.payload.invitationStatus,
        billingValid: action.payload.billingValid,
        accountStatus: action.payload.accountStatus,
        identityVerificationStatus: action.payload.identityVerificationStatus,
        identityVerificationActionUrl: action.payload.identityVerificationActionUrl,
      }
    case googleActions.SET_INVITATION_TIMED_OUT:
      return { ...state, loading: false, invitationTimedOut: true }
    case googleActions.SET_CONFIRM_CONNECTION_IN_FLIGHT:
      return { ...state, confirmConnectionInFlight: action.payload }
    case googleActions.SET_ASSETS: {
      const { data, error } = action.payload
      return {
        ...state,
        loading: false,
        assets: data ?? initialState.assets,
        error: error ?? state.error,
      }
    }
    case googleActions.SET_JUST_SETTLED:
      return { ...state, justSettled: action.payload }
    case googleActions.SET_GOOGLE_SESSION: {
      const sessionKey = get_google_sessions_key()
      localStorage.setItem(sessionKey, JSON.stringify(action.payload))
      return { ...state, googleSession: action.payload }
    }
    case googleActions.SET_ANONYMOUS:
      return { ...state, isAnonymous: true }
    case googleActions.RESET_TO_IDLE:
      return { ...initialState }
    default:
      return state
  }
}

const POLL_INTERVAL_MS = 4000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

export const asyncActionHandlers = {
  [googleActions.GET_STATUS]:
    ({ dispatch, getState }) =>
      async () => {
        const result = await googleAuthorizationApi.getStatus()
        dispatch({ type: googleActions.SET_STATUS, payload: result })
        // For anonymous context: load assets after status is fetched
        if (getState().isAnonymous) {
          dispatch({ type: googleActions.GET_ASSETS })
        }
      },

  [googleActions.GET_ASSETS]:
    ({ dispatch, getState }) =>
      async () => {
        const { isAnonymous } = getState()
        const sessionKey = get_google_sessions_key()
        let cachedSession = localStorage.getItem(sessionKey)
        cachedSession = cachedSession ? JSON.parse(cachedSession) : initialState.googleSession
        if (isAnonymous && !cachedSession.loggedIn) {
          dispatch({ type: googleActions.SET_ASSETS, payload: { data: [] } })
          return
        }
        const platformUserIds = isAnonymous ? [cachedSession.platformUserId] : null
        const result = await googleAssetsApi.listAssets(platformUserIds)
        dispatch({ type: googleActions.SET_ASSETS, payload: result })
      },

  [googleActions.AUTHORIZE]:
    ({ dispatch }) =>
      async (action) => {
        dispatch({ type: googleActions.SET_LOADING })
        const { code, redirectUri } = action.payload
        const result = await googleAuthorizationApi.authorize(code, redirectUri)
        if (result.error) {
          dispatch({ type: googleActions.SET_ERROR, payload: result.error })
          return
        }
        const { accounts, platform_user_id } = result.data
        if (!accounts || accounts.length === 0) {
          dispatch({ type: googleActions.SET_ERROR, payload: null })
          dispatch({ type: googleActions.SET_STEP, payload: googleSteps.NO_ACCOUNTS })
          return
        }
        // Write to localStorage directly before dispatching, same as Meta pattern —
        // SET_GOOGLE_SESSION reducer runs async so GET_ASSETS would read stale session
        const sessionKey = get_google_sessions_key()
        const session = { platformUserId: platform_user_id, loggedIn: true }
        localStorage.setItem(sessionKey, JSON.stringify(session))
        dispatch({ type: googleActions.SET_GOOGLE_SESSION, payload: session })
        dispatch({ type: googleActions.SET_ACCOUNTS, payload: accounts })
        dispatch({ type: googleActions.SET_STEP, payload: googleSteps.ACCOUNT_SELECTION })
      },

  [googleActions.SELECT_ACCOUNT]:
    ({ dispatch }) =>
      async (action) => {
        dispatch({ type: googleActions.SET_LOADING })
        const result = await googleAssetsApi.selectAccount(action.payload)
        if (result.error) {
          dispatch({ type: googleActions.SET_MODAL_ERROR, payload: result.error })
          return
        }
        dispatch({
          type: googleActions.SET_SELECTED_ACCOUNT,
          payload: {
            customerId: action.payload.customer_id,
            accountName: action.payload.descriptive_name,
          },
        })
        dispatch({ type: googleActions.SET_STEP, payload: googleSteps.INVITATION_PENDING })
        dispatch({
          type: googleActions.POLL_INVITATION,
          payload: { customerId: action.payload.customer_id },
        })
      },

  [googleActions.POLL_INVITATION]:
    ({ dispatch, signal }) =>
      async (action) => {
        const { customerId } = action.payload

        // Self-rescheduling setTimeout, not setInterval: the next tick is only armed
        // after the previous confirmConnection call resolves, so a slow backend never
        // lets requests pile up (confirmConnection can take well over POLL_INTERVAL_MS).
        let stopped = false
        let pollTimeoutId

        const stopPolling = () => {
          stopped = true
          clearTimeout(pollTimeoutId)
        }

        const overallTimeoutId = setTimeout(() => {
          if (signal.aborted || stopped) return
          stopPolling()
          dispatch({ type: googleActions.SET_INVITATION_TIMED_OUT })
        }, POLL_TIMEOUT_MS)

        const tick = async () => {
          if (signal.aborted || stopped) return

          dispatch({ type: googleActions.SET_CONFIRM_CONNECTION_IN_FLIGHT, payload: true })
          const result = await googleAssetsApi.confirmConnection(customerId)
          dispatch({ type: googleActions.SET_CONFIRM_CONNECTION_IN_FLIGHT, payload: false })

          if (signal.aborted || stopped) return

          if (result.error) {
            stopPolling()
            clearTimeout(overallTimeoutId)
            dispatch({ type: googleActions.SET_MODAL_ERROR, payload: result.error })
            return
          }

          const {
            invitation_status,
            billing_valid,
            account_status,
            identity_verification_status,
            identity_verification_action_url,
          } = result.data
          dispatch({
            type: googleActions.SET_INVITATION,
            payload: {
              invitationStatus: invitation_status,
              billingValid: billing_valid,
              accountStatus: account_status,
              identityVerificationStatus: identity_verification_status,
              identityVerificationActionUrl: identity_verification_action_url,
            },
          })

          if (invitation_status === 'ACTIVE' && account_status === 'enabled') {
            stopPolling()
            clearTimeout(overallTimeoutId)
            dispatch({ type: googleActions.GET_STATUS })
            dispatch({ type: googleActions.GET_ASSETS })
            return
          }
          if (invitation_status === 'REFUSED') {
            stopPolling()
            clearTimeout(overallTimeoutId)
            return
          }

          // Continue polling if account is 'not_enabled' even if invitation is ACTIVE
          pollTimeoutId = setTimeout(tick, POLL_INTERVAL_MS)
        }

        pollTimeoutId = setTimeout(tick, POLL_INTERVAL_MS)
      },

  [googleActions.CREATE_ACCOUNT]:
    ({ dispatch }) =>
      async (action) => {
        dispatch({ type: googleActions.SET_LOADING })
        const result = await googleAssetsApi.createAccount(action.payload)
        if (result.error) {
          dispatch({ type: googleActions.SET_ERROR, payload: result.error })
          return
        }
        const { customer_id } = result.data
        dispatch({ type: googleActions.SET_SELECTED_ACCOUNT, payload: customer_id })
        dispatch({
          type: googleActions.SET_INVITATION,
          payload: {
            invitationStatus: 'ACTIVE',
            billingValid: false,
            accountStatus: 'not_enabled',
          },
        })
        dispatch({ type: googleActions.SET_STEP, payload: googleSteps.INVITATION_PENDING })
        dispatch({ type: googleActions.GET_ASSETS })
        dispatch({ type: googleActions.GET_STATUS })
        // Start polling for account activation
        dispatch({
          type: googleActions.POLL_INVITATION,
          payload: { customerId: customer_id },
        })
        window.open(GOOGLE_ADS_BILLING_URL, '_blank', 'noopener,noreferrer')
      },

  [googleActions.REFRESH_BILLING]:
    ({ dispatch, getState }) =>
      async (action) => {
        // Skip if a POLL_INVITATION tick already has a confirmConnection call in
        // flight for this same slow endpoint, so a manual refresh doesn't double up.
        if (getState().confirmConnectionInFlight) return
        const { customerId } = action.payload
        dispatch({ type: googleActions.SET_CONFIRM_CONNECTION_IN_FLIGHT, payload: true })
        const result = await googleAssetsApi.confirmConnection(customerId)
        dispatch({ type: googleActions.SET_CONFIRM_CONNECTION_IN_FLIGHT, payload: false })
        if (result.error) return
        const {
          invitation_status,
          billing_valid,
          account_status,
          identity_verification_status,
          identity_verification_action_url,
        } = result.data
        dispatch({
          type: googleActions.SET_INVITATION,
          payload: {
            invitationStatus: invitation_status,
            billingValid: billing_valid,
            accountStatus: account_status,
            identityVerificationStatus: identity_verification_status,
            identityVerificationActionUrl: identity_verification_action_url,
          },
        })
        if (billing_valid) {
          dispatch({ type: googleActions.GET_ASSETS })
        }
      },

  [googleActions.DISCONNECT]:
    ({ dispatch }) =>
      async (action) => {
        dispatch({ type: googleActions.SET_LOADING })
        await googleAuthorizationApi.disconnect(action.payload?.customerId)
        dispatch({
          type: googleActions.SET_GOOGLE_SESSION,
          payload: { platformUserId: null, loggedIn: false },
        })
        dispatch({ type: googleActions.RESET_TO_IDLE })
        dispatch({ type: googleActions.GET_STATUS })
        dispatch({ type: googleActions.GET_ASSETS })
      },

  [googleActions.TOGGLE_ASSET]:
    ({ dispatch }) =>
      async (action) => {
        const { assetId, enabled } = action.payload
        await googleAssetsApi.toggleAsset(assetId, enabled)
        dispatch({ type: googleActions.GET_ASSETS })
      },
}

export const GoogleConnectContextProvider = ({ children }) => {
  const [state, dispatch] = useReducerAsync(reducer, initialState, asyncActionHandlers)

  useEffect(() => {
    const sessionKey = get_google_sessions_key()
    const cached = localStorage.getItem(sessionKey)
    if (!cached) return
    const session = JSON.parse(cached)
    const hasExpired = session.expiresAt && Date.now() > session.expiresAt
    dispatch({
      type: googleActions.SET_GOOGLE_SESSION,
      payload: hasExpired ? { ...session, loggedIn: false } : session,
    })
  }, [dispatch])

  const isConnected = useCallback(
    () => state.partnerConnectionType === 'default',
    [state.partnerConnectionType],
  )

  return (
    <GoogleConnectContext.Provider value={state}>
      <GoogleConnectDispatchContext.Provider value={{ dispatch, isConnected }}>
        {children}
      </GoogleConnectDispatchContext.Provider>
    </GoogleConnectContext.Provider>
  )
}

GoogleConnectContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export function useGoogleConnectContext() {
  const state = React.useContext(GoogleConnectContext)
  if (!state) {
    throw new Error('useGoogleConnectContext must be used within a GoogleConnectContextProvider')
  }
  return state
}

export function useGoogleConnectDispatchContext() {
  const ctx = React.useContext(GoogleConnectDispatchContext)
  if (!ctx) {
    throw new Error(
      'useGoogleConnectDispatchContext must be used within a GoogleConnectContextProvider',
    )
  }
  return ctx
}
